import { useMemo } from 'react';
import { formatDate } from 'src/utils';
import { ListJobManhoursResponse } from 'src/api/FiretrolApiClient';
import { ManhoursChartData } from 'src/routes/Manhours/components/ManhoursChart';
import { ManhoursTableRow } from 'src/routes/Manhours/components/ManhoursTable';

export interface DatePeriod {
  id: string;
  label: string;
}

interface ManhoursData {
  datePeriods: DatePeriod[];
  summary: Record<string, number>;
  chartData: ManhoursChartData[];
  rows: ManhoursTableRow[];
  totalCtcHours: number;
}

export const useManhoursData = (
  response: ListJobManhoursResponse | undefined,
): ManhoursData =>
  useMemo(() => {
    const weeks = response?.weeks ?? [];
    const datePeriods = weeks.map(
      (week): DatePeriod => ({
        id: formatDate(week.weekStart, 'YYYY-MM-DD'),
        label: formatDate(week.weekStart, 'MM/DD'),
      }),
    );

    const summary: Record<string, number> = {};
    weeks.forEach((week, index) => {
      summary[datePeriods[index].id] = week.totalFte;
    });

    const chartData = weeks.map((week, index) => ({
      name: datePeriods[index].label,
      FTEs: week.totalFte,
    }));

    const rows = (response?.rows ?? []).map((m): ManhoursTableRow => {
      const jobNumber = m.wsJobNumber.trim();
      const weeklyFtes: Record<string, number> = {};
      datePeriods.forEach((period, index) => {
        weeklyFtes[period.id] = m.weeklyFtes[index] ?? 0;
      });

      return {
        id: `${jobNumber}::${m.costCode}`,
        jobId: m.jobId ?? null,
        jobNumber,
        jobName: m.wsJobName.trim(),
        costCode: m.costCode,
        ctcHours: m.ctcHours,
        liquidatedDamages: m.liquidatedDamages,
        startDate: formatDate(m.startDate, 'DD MMM YYYY') || null,
        endDate: formatDate(m.endDate, 'DD MMM YYYY') || null,
        weeklyFtes,
      };
    });

    return {
      datePeriods,
      summary,
      chartData,
      rows,
      totalCtcHours: response?.totalCtcHours ?? 0,
    };
  }, [response]);
